/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  getChatAnswer,
  fetchSpeech,
  createAssistantThread,
  getAnswerFromThreads,
  fetchGenkitSpeech,
  getGenkitChatAnswer,
} from "../../api/chat";
import { useAudioController } from "../../hooks/useAudioController";
import { getChatConfigs, getSpeechConfigs } from "../../config/database/chat";
import "./voiceAssistant.css";
import Typewriter from "../../components/Typewriter";
import audioWaveGif from "../../assets/Images/audio-wave.gif";
import { useSelector } from "react-redux";
import { getChat, getForm } from "../../store/apps/chat";
import { getDataFromUrl } from "../../config/database/assistants";
import MarkdownIt from "markdown-it";
import hljs from "highlight.js";
import "highlight.js/styles/github.css";
import { processMessageContent } from "../../util/utils";

const VoiceAssistant = () => {
  const chat = useSelector(getChat);
  const form = useSelector(getForm);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [chatConfigData, setChatConfigData] = useState();
  const [divHeight, setDivHeight] = useState(0);
  const [speechConfigData, setSpeechConfigData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [speakingStates, setSpeakingStates] = useState({});
  const [recognition, setRecognition] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [isWordRecognized, setIsWordRecognized] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [typewriter, setTypewriter] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [assistantData, setAssistantData] = useState();
  const [assistantId, setAssistantId] = useState("");
  const [threadId, setThreadId] = useState("");

  const [inputSpeechLanguage, setInputSpeechLanguage] = useState("no-NO");

  // Use centralized audio controller
  const { executeAudioOperation, stopAllAudio } = useAudioController();

  // Refs
  const divRef = useRef(null);
  // const messageContainerRef = useRef(null);
  const inputValueRef = useRef();
  const chatConfigDataRef = useRef(null);
  const speechConfigDataRef = useRef(null);
  const pendingMessagesRef = useRef(null);
  const scrollRef = useRef(null);

  // Speech Config Data
  useEffect(() => {
    const fetchSpeechConfigData = async () => {
      const configData = await getSpeechConfigs();

      const newSpeechConfig =
        form?.speechModel && form?.speechVoice
          ? {
            ...configData,
            model: form.speechModel,
            voice: form.speechVoice,
          }
          : configData;

      speechConfigDataRef.current = newSpeechConfig;
      setSpeechConfigData(newSpeechConfig);
      // speechConfigDataRef.current = configData;
      // setSpeechConfigData(configData);
    };

    fetchSpeechConfigData();
  }, []);

  // Chat Config Data
  useEffect(() => {
    const fetchChatConfig = async () => {
      const data = await getChatConfigs();

      const newChatConfig = form?.chatModel
        ? {
          ...data,
          model: form.chatModel,
        }
        : data;

      chatConfigDataRef.current = newChatConfig;
      setChatConfigData(newChatConfig);
      // chatConfigDataRef.current = data;
      // setChatConfigData(data);
    };

    fetchChatConfig();
  }, []);

  // Height Update
  useEffect(() => {
    const updateHeight = () => {
      if (divRef.current) {
        setDivHeight(divRef.current.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Check Device Type
  useEffect(() => {
    const checkIfTouchDevice = () => {
      setIsTouchDevice(
        "ontouchstart" in window || navigator.maxTouchPoints > 0
      );
    };

    checkIfTouchDevice();
  }, []);

  // For InputValue Ref
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  const initializeSpeechRecognition = () => {
    if (!("webkitSpeechRecognition" in window)) {
      console.error("Speech recognition not supported in this browser.");
      return;
    }

    const recognitionInstance = new window.webkitSpeechRecognition();
    recognitionInstance.continuous = true;
    recognitionInstance.interimResults = true;
    recognitionInstance.lang = inputSpeechLanguage; //"en-US";

    recognitionInstance.onstart = () => {
      setIsRecognizing(true);
    };

    recognitionInstance.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      // Set Input based on platform (Specially android)
      if (window.navigator.platform.toLowerCase().includes("linux arm")) {
        setInputValue(event.results[event.results.length - 1][0].transcript);
      } else {
        setInputValue(final + interim);
      }
      setIsWordRecognized(final.length > 0 || interim.length > 0);
    };

    recognitionInstance.onerror = (event) => {
      console.error("Speech recognition error", event);
    };

    recognitionInstance.onend = () => {
      setIsRecognizing(false);
      setIsWordRecognized(false);
      if (inputValueRef.current.trim()) {
        sendMessage(inputValueRef.current.trim());
      }
    };

    setRecognition(recognitionInstance);

    // Start recognition on function call
    recognitionInstance.start();
  };

  // Send Message to Server
  const sendMessage = async (data) => {
    if (!data) return;

    if (
      messages.length > 1 &&
      messages[messages.length - 1].content === "Typing..."
    ) {
      return;
    }

    const userMessage = {
      role: "user",
      content: data,
    };
    // Include user message
    const newMessages = [...messages, userMessage];

    // Typing indicator
    const typingMessage = {
      role: "assistant",
      content: "Typing...",
      typing: true,
    };

    setMessages([typingMessage]);
    setInputValue("");
    setTypewriter(true);

    const relevantMessages = newMessages.slice(-7);
    setIsResponding(true);

    try {
      let response;
      if (assistantId && threadId) {
        response = await getAnswerFromThreads({
          messages: data,
          assistantId,
          threadId,
        });
      } else {
        // Determine chat gateway
        const chatSettings = form?.chatSettings;
        const useDefaultChatSettings =
          chatSettings?.useDefaultChatSettings !== undefined
            ? chatSettings.useDefaultChatSettings
            : true;

        const effectiveChatConfig = !useDefaultChatSettings
          ? { ...chatConfigDataRef.current, ...chatSettings }
          : chatConfigDataRef.current;

        const systemInstructions =
          effectiveChatConfig?.systemInstructions ||
          effectiveChatConfig?.systemInstruction ||
          assistantData?.systemInstructions ||
          "";

        const chatGateway = effectiveChatConfig?.gateway || "openai";
        const isGenkit = chatGateway === "genkit";

        if (isGenkit) {
          response = await getGenkitChatAnswer({
            messages: relevantMessages,
            model: effectiveChatConfig?.model,
            temperature: effectiveChatConfig?.temperature,
            topP: effectiveChatConfig?.topP,
            topK: effectiveChatConfig?.topK,
            maxOutputTokens: effectiveChatConfig?.maxOutputTokens,
            thinkingBudget: effectiveChatConfig?.thinkingBudget,
            codeExecution: effectiveChatConfig?.codeExecution,
            googleSearch: effectiveChatConfig?.googleSearch,
            safetySettings: effectiveChatConfig?.safetySettings,
            systemInstructions,
          });
        } else {
          response = await getChatAnswer({
            messages: relevantMessages,
            ...effectiveChatConfig,
            systemInstructions,
          });
        }
      }

      // Determine chat gateway for response parsing
      const chatSettings = form?.chatSettings;
      const useDefaultChatSettings =
        chatSettings?.useDefaultChatSettings !== undefined
          ? chatSettings.useDefaultChatSettings
          : true;
      const effectiveChatConfig = !useDefaultChatSettings
        ? { ...chatConfigDataRef.current, ...chatSettings }
        : chatConfigDataRef.current;
      const chatGateway = effectiveChatConfig?.gateway || "openai";
      const isGenkit = chatGateway === "genkit";

      const assistantReply = {
        role: "assistant",
        content:
          assistantId && threadId
            ? response.response
            : isGenkit
              ? response.response
              : response.choices[0].message.content,
      };
      // const updatedMessages = [...newMessages, assistantReply];
      const updatedMessages = [assistantReply];
      // setMessages(updatedMessages);
      pendingMessagesRef.current = updatedMessages;

      speakMessage(assistantReply, updatedMessages.length - 1);
    } catch (error) {
      const errorMessage = {
        role: "assistant",
        type: "error",
        content: `Sorry, an error occurred while processing your request. Please try again later.Error:Code: ${error?.response?.status} Message: ${error?.response?.data} `,
      };
      setIsResponding(false);
      const updatedMessagesWithError = [errorMessage];
      setMessages(updatedMessagesWithError);
    }
  };

  // Speak the response from server
  const speakMessage = async (message, index) => {
    setSpeakingStates((prev) => ({ ...prev, [index]: true }));
    setIsSpeaking(true);
    // Process message content to remove hidden color codes for speech
    const { speechText } = processMessageContent(message.content);

    // Determine speech gateway
    const speechSettings = form?.speechSettings;
    const useDefaultSpeechSettings =
      speechSettings?.useDefaultSpeechSettings !== undefined
        ? speechSettings.useDefaultSpeechSettings
        : true;

    const effectiveSpeechConfig = !useDefaultSpeechSettings
      ? { ...speechConfigDataRef.current, ...speechSettings }
      : speechConfigDataRef.current;

    const speechGateway = effectiveSpeechConfig?.gateway || "openai";
    const isGenkitSpeech = speechGateway === "genkit";
    const speechFn = isGenkitSpeech ? fetchGenkitSpeech : fetchSpeech;

    const speechConfig =
      assistantData &&
        !assistantData?.defaultTtsConfig &&
        assistantData?.tts_model &&
        assistantData?.tts_speed &&
        assistantData?.tts_voice
        ? {
          text: speechText,
          model: assistantData?.tts_model,
          speed: assistantData?.tts_speed,
          voice: assistantData?.tts_voice,
        }
        : {
          text: speechText,
          ...effectiveSpeechConfig,
        };

    await executeAudioOperation(
      (signal) => speechFn(speechConfig, signal, handleChunkFetched),
      () => {
        setSpeakingStates((prev) => ({ ...prev, [index]: false }));
        setIsSpeaking(false);
        setTypewriter(false);
      },
      (error) => {
        console.error("Error during speech synthesis:", error);
        setSpeakingStates((prev) => ({ ...prev, [index]: false }));
        setIsSpeaking(false);
        setTypewriter(false);
      }
    );
  };

  const handleChunkFetched = () => {
    if (pendingMessagesRef.current) {
      setIsResponding(false);
      setMessages(pendingMessagesRef.current);
      pendingMessagesRef.current = null;
    }
  };

  const handleStopSpeech = () => {
    stopAllAudio();
    setSpeakingStates({});
    setIsRecognizing(false);
    setIsSpeaking(false);
    setMessages([]);
  };

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleWordTyped = useCallback(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, []);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const typewriterComponent = useMemo(() => {
    // Process message content to remove hidden color codes for display
    const { displayText } = messages[0]?.content ? processMessageContent(messages[0].content) : { displayText: '' };
    return (
      <Typewriter
        texts={[displayText]}
        speed={35}
        startDelay={0}
        onWordTyped={handleWordTyped}
      />
    );
  }, [messages[0]?.content]);

  useEffect(() => {
    const button = document.getElementById("voice-assistant-button");

    const handleTouchStart = (e) => {
      e.preventDefault();
      if (!isRecognizing && !isSpeaking && !isResponding) {
        initializeSpeechRecognition();
      }
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      if (isRecognizing) {
        recognition.stop();
      }
    };

    button.addEventListener("touchstart", handleTouchStart);
    button.addEventListener("touchend", handleTouchEnd);

    return () => {
      button.removeEventListener("touchstart", handleTouchStart);
      button.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    isRecognizing,
    isSpeaking,
    isResponding,
    recognition,
    inputSpeechLanguage,
  ]);

  useEffect(() => {
    if (chat.length > 0 && Object.keys(speechConfigData).length > 0) {
      setMessages([...messages, chat[1]]);
      speakMessage(chat[1], 0);
      chat[1]?.assistantThreadId && setThreadId(chat[1]?.assistantThreadId);
    }
  }, [chat, speechConfigData]);

  useEffect(() => {
    if (chat.length > 0 && Object.keys(speechConfigData).length > 0) {
      chat[1]?.assistantThreadId && setThreadId(chat[1]?.assistantThreadId);
    }
  }, [chat, speechConfigData, threadId]);

  useEffect(() => {
    checkValidUrl();
  }, []);

  const checkValidUrl = async () => {
    const pathname = decodeURIComponent(window.location.pathname);

    const urlParts = pathname.split("/").filter((part) => part);
    if (urlParts.length >= 2) {
      const route = urlParts[urlParts.length - 2]; // second last part
      const routeId = urlParts[urlParts.length - 1]; // last part

      if (route === "assistants") {
        const assistant = await getDataFromUrl(routeId);
        if (assistant.length > 0) {
          setAssistantId(assistant[0]?.assistantId);
          setAssistantData(assistant[0]);
          setInputSpeechLanguage(assistant[0]?.inputSpeechLanguage || "no-NO");
          if (threadId.length === 0) {
            const thread = await createAssistantThread();
            setThreadId(thread?.id);
          }
        }
      }
    }
  };

  const md = new MarkdownIt({
    highlight: function (str, lang) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return (
            '<pre class="hljs"><code>' +
            hljs.highlight(str, { language: lang, ignoreIllegals: true })
              .value +
            "</code></pre>"
          );
        } catch (__) { }
      }

      return (
        '<pre class="hljs"><code>' + md.utils.escapeHtml(str) + "</code></pre>"
      );
    },
  });

  return (
    <div className="flex flex-col">
      <div ref={divRef}></div>
      <div
        className={`flex justify-center items-center w-full
          bg-gradient-to-r from-[#232526] to-[#414345]
          px-[5%] md:px-[10%]`}
        style={{ height: `calc(100vh - ${divHeight}px)` }}
      >
        <div className={`flex justify-center items-center flex-row w-full`}>
          {/* Speech Text */}
          <div className="flex justify-center items-center w-[50%]">
            <div
              className="w-[100%] md:max-w-[85%] max-h-[80vh] overflow-y-auto"
              ref={scrollRef}
            >
              <p
                className={`flex items-center text-light_gray text-[16px] leading-[24px] md:text-[20px] md:leading-[32px] font-poppins font-[500] mt-[20px]`}
              >
                {isRecognizing
                  ? inputValue
                  : messages[0]?.content && (() => {
                    // Process message content to remove hidden color codes for display
                    const { displayText } = processMessageContent(messages[0].content);
                    return messages[0]?.content && typewriter ? (
                      typewriterComponent
                    ) : (
                      <div
                        className={` md:mt-[5px]`}
                        dangerouslySetInnerHTML={{
                          __html: md.render(displayText),
                        }}
                      />
                    );
                  })()}
              </p>
            </div>
          </div>

          {/* Mic Button */}
          <div className="flex flex-col justify-center items-center w-[50%]">
            <div className=" flex justify-center items-center">
              {(isWordRecognized || isSpeaking) && !isResponding && (
                <div
                  className="absolute pulse-ring overflow-auto w-[80px] h-[80px] 
              md:w-[110px] md:h-[110px] lg:w-[150px] lg:h-[150px]"
                ></div>
              )}

              <button
                id="voice-assistant-button"
                className={`relative flex justify-center items-center overflow-hidden rounded-[50%] w-[80px] h-[80px] 
              md:w-[110px] md:h-[110px] lg:w-[150px] lg:h-[150px] shadow-lg border-[2px] cursor-pointer
              ${isWordRecognized ? "border-none" : "border-[#FFF]"}
              ${isWordRecognized ? "bg-[#FF0000]" : "bg-[#38393B]"}
              `}
                style={{
                  transition: "all .5s ease",
                }}
                onClick={() => {
                  if (isSpeaking && !isResponding) {
                    handleStopSpeech();
                  }
                }}
                onMouseDown={() => {
                  if (!isRecognizing && !isSpeaking && !isResponding) {
                    initializeSpeechRecognition();
                  }
                }}
                onMouseUp={() => {
                  if (isRecognizing) {
                    recognition.stop();
                  }
                }}
                onTouchEnd={() => {
                  if (isSpeaking && !isResponding) {
                    handleStopSpeech();
                  }
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {!isTouchDevice ? (
                  <div className="flex items-center justify-center cursor-pointer">
                    {isResponding ? (
                      <img
                        src={audioWaveGif}
                        alt="wave"
                        className="text-[24px] md:text-[36px] lg:text-[45px]"
                        style={{ filter: "invert(1)" }}
                      />
                    ) : isSpeaking ? (
                      isHovered ? (
                        <i
                          className="fa-solid fa-stop text-[24px] md:text-[36px] lg:text-[45px]"
                          style={{
                            color: "#FFF",
                          }}
                          aria-hidden="true"
                        ></i>
                      ) : (
                        <i
                          className={`fa-solid fa-volume-high text-[24px] md:text-[36px] lg:text-[45px]`}
                          style={{
                            color: "#FFF",
                          }}
                          aria-hidden="true"
                        ></i>
                      )
                    ) : (
                      <i
                        className={`fa fa-microphone text-[24px] md:text-[36px] lg:text-[45px]`}
                        style={{
                          color: isRecognizing
                            ? isWordRecognized
                              ? "#FFF"
                              : "#FF0000"
                            : "#FFF",
                        }}
                        aria-hidden="true"
                      ></i>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center cursor-pointer">
                    {isResponding ? (
                      <img
                        src={audioWaveGif}
                        alt="wave"
                        className="text-[24px] md:text-[36px] lg:text-[45px]"
                        style={{ filter: "invert(1)" }}
                      />
                    ) : isSpeaking ? (
                      <i
                        className={`fa-solid fa-volume-high text-[24px] md:text-[36px] lg:text-[45px]`}
                        style={{
                          color: "#FFF",
                        }}
                        aria-hidden="true"
                      ></i>
                    ) : (
                      <i
                        className={`fa fa-microphone text-[24px] md:text-[36px] lg:text-[45px]`}
                        style={{
                          color: isRecognizing
                            ? isWordRecognized
                              ? "#FFF"
                              : "#FF0000"
                            : "#FFF",
                        }}
                        aria-hidden="true"
                      ></i>
                    )}
                  </div>
                )}
              </button>
            </div>
            {isSpeaking && !isResponding && isTouchDevice && (
              <div className="text-white mt-[30px] text-[12px] md:text-[14px]">
                Tap the button to stop listening.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAssistant;
